import { Link, Outlet, useLocation, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/context/authStore';
import { useEnsureTenant } from '@/hooks/useTenant';
import { useSyncCurrentUser } from '@/hooks/useCurrentUser';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import InstallPWAButton from '@/components/InstallPWAButton';
import {
  LayoutDashboard,
  FlaskConical,
  LogOut,
  Menu,
  X,
  Key,
  LucideIcon,
  Building2,
  Users,
  FileText,
  Database,
  PawPrint,
} from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
}

// Navegacion del Lab Panel
const labNavigation: NavItem[] = [
  { name: 'Inicio', href: '/lab', icon: LayoutDashboard },
  { name: 'Clinicas', href: '/lab/clinics', icon: Building2 },
  { name: 'Pacientes', href: '/lab/patients', icon: Users },
  { name: 'Protocolos', href: '/lab/protocols', icon: FileText },
  { name: 'Valores de Ref.', href: '/lab/reference-values', icon: Database },
  { name: 'Especies', href: '/lab/species', icon: PawPrint },
];

export default function LabLayout() {
  useEnsureTenant(); // Red de seguridad: tenant siempre del usuario logueado
  useSyncCurrentUser(); // Permisos siempre frescos (si el admin los cambia, no hay que re-loguearse)
  const { user, logout, tenant } = useAuthStore();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Permitir acceso si es laboratory o si está impersonando (superadmin)
  const isImpersonating = useAuthStore((s) => s.impersonation.isImpersonating);
  if (user?.role !== 'laboratory' && !isImpersonating) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-lab-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-lab-900/50 backdrop-blur-sm z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-30 w-64 bg-lab-800 shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-lab-700 safe-top">
            <Link to="/lab" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-lab-400 to-lab-600 rounded-lg flex items-center justify-center">
                <FlaskConical className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">ANIMA Lab</span>
            </Link>
            <button
              className="lg:hidden p-2 rounded-md hover:bg-lab-700 text-lab-300"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Lab Badge */}
          <div className="px-4 py-3 border-b border-lab-700">
            <div className="flex items-center gap-2 px-3 py-2 bg-lab-500/10 rounded-lg">
              <div className="w-2 h-2 bg-lab-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-lab-200">Laboratorio</span>
            </div>
            {tenant && (
              <p className="mt-2 text-xs text-lab-300 px-3">{tenant.name}</p>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {labNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={clsx(
                    'flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                    isActive
                      ? 'bg-lab-500 text-white shadow-lg shadow-lab-500/30'
                      : 'text-lab-200 hover:bg-lab-700 hover:text-white'
                  )}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Botón instalar como app (PWA) */}
          <div className="px-2 pb-2">
            <InstallPWAButton />
          </div>

          {/* User section */}
          <div className="border-t border-lab-700 p-4 safe-bottom">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-lab-400 to-lab-600 flex items-center justify-center">
                  <span className="text-white font-bold">
                    {user?.full_name.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.full_name}
                </p>
                <p className="text-xs text-lab-300 truncate">{user?.email}</p>
              </div>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="p-2 rounded-md hover:bg-lab-700 text-lab-300 hover:text-white"
                title="Cambiar contrasena"
              >
                <Key className="h-5 w-5" />
              </button>
              <button
                onClick={logout}
                className="p-2 rounded-md hover:bg-lab-700 text-lab-300 hover:text-white"
                title="Cerrar sesion"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de cambio de contrasena */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-auto">
        {/* Top bar (mobile) */}
        <div className="lg:hidden sticky top-0 z-10 bg-lab-800 shadow-lg safe-top">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              className="p-2 rounded-md hover:bg-lab-700 text-lab-300"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-lab-400 to-lab-600 rounded-lg flex items-center justify-center">
                <FlaskConical className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">Lab</span>
            </div>
            <div className="w-10" />
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
